import type { Firestore } from 'firebase-admin/firestore'
import { isString, isNonEmptyString } from '../../common/utils/type-guards.js'
import type { RedisClient } from '../../core/redis/client.js'
import type {
  BadgeId,
  BadgeTemplate,
  CreateQuestTemplateDTO,
  CreateChallengeTemplateDTO,
  UpdateQuestTemplateDTO,
  UpdateChallengeTemplateDTO,
  QuestTemplate,
  ChallengeTemplate,
  SeasonConfig,
  CreateSeasonConfigDTO,
  UpdateSeasonConfigDTO
  ,CreateBadgeDTO,
  UpdateBadgeDTO
} from '@my-roadtrip/types'
import { NotFoundError, ConflictError } from '../../common/errors/index.js'
import { createLogger } from '../../common/utils/logger.js'
import { createBatchBuilder } from '../../common/utils/firestore-batch.js'
import { OpenAIService } from '../../services/openai.service.js'
import type { FirestoreDoc, FirestoreDocLike } from '../../common/types/firestore.js'

/**
 * Gamification Admin Service - Full CRUD for quests, challenges, seasons, badges
 * 
 * Features:
 * - Quest template management (create, update, delete, list)
 * - Challenge template management (create, update, delete, list)
 * - Season configuration (create, update, delete, activate)
 * - Badge management (create, update, delete, assign)
 * - Bulk operations (import/export templates)
 * - Cache invalidation on changes
 * - Structured error handling
 */
export class GamificationAdminService {
  private readonly logger = createLogger('GamificationAdminService')
  private readonly openai = new OpenAIService()

  constructor(
    private readonly firestore: Firestore,
    private readonly redis: RedisClient
  ) {}

  // ========== QUEST TEMPLATE MANAGEMENT ==========

  /**
   * Create a new quest template
   */
  async createQuestTemplate(data: CreateQuestTemplateDTO): Promise<QuestTemplate> {
    const ref = this.firestore.collection('questTemplates').doc()
    
    const template: QuestTemplate = {
      id: ref.id,
      type: data.type,
      category: data.category,
      title: data.title,
      description: data.description,
      difficulty: data.difficulty,
      targetValue: data.targetValue,
      reward: data.reward,
      enabled: data.enabled ?? true,
      weight: data.weight ?? 1,
      requirements: data.requirements,
      tags: Array.isArray(data.tags)
        ? (data.tags as unknown[]).map((tag) => String(tag ?? '').trim()).filter(Boolean)
        : [],
      createdAt: new Date(),
      updatedAt: new Date()
    }

    await ref.set(template)
    
    // Invalidate quest template cache
    await this.invalidateQuestTemplateCache()

    return template
  }

  /**
   * Update an existing quest template
   */
  async updateQuestTemplate(
    templateId: string,
    data: UpdateQuestTemplateDTO
  ): Promise<QuestTemplate> {
    const ref = this.firestore.collection('questTemplates').doc(templateId)
    const doc = await ref.get()

    if (!doc.exists) {
      throw new NotFoundError('Quest template not found', { templateId })
    }

    const updates = {
      ...data,
      updatedAt: new Date()
    }

    await ref.update(updates)

    const updated = await ref.get()
    const template = this.mapDocToQuestTemplate(updated)

    // Invalidate cache
    await this.invalidateQuestTemplateCache()

    return template
  }

  /**
   * Delete a quest template
   */
  async deleteQuestTemplate(templateId: string): Promise<void> {
    const ref = this.firestore.collection('questTemplates').doc(templateId)
    const doc = await ref.get()

    if (!doc.exists) {
      throw new NotFoundError('Quest template not found', { templateId })
    }

    // Soft delete (mark as disabled)
    await ref.update({
      enabled: false,
      deletedAt: new Date(),
      updatedAt: new Date()
    })

    // Invalidate cache
    await this.invalidateQuestTemplateCache()
  }

  /**
   * Get all quest templates
   */
  async getQuestTemplates(includeDisabled = false): Promise<QuestTemplate[]> {
    let query = this.firestore.collection('questTemplates').orderBy('category').orderBy('difficulty')

    if (!includeDisabled) {
      query = query.where('enabled', '==', true)
    }

    const snapshot = await query.get()
    return snapshot.docs.map((doc: FirestoreDoc) => this.mapDocToQuestTemplate(doc))
  }

  /**
   * Get quest template by ID
   */
  async getQuestTemplateById(templateId: string): Promise<QuestTemplate | null> {
    const doc = await this.firestore.collection('questTemplates').doc(templateId).get()
    if (!doc.exists) return null
    return this.mapDocToQuestTemplate(doc)
  }

  // ========== CHALLENGE TEMPLATE MANAGEMENT ==========

  /**
   * Create a new challenge template
   */
  async createChallengeTemplate(data: CreateChallengeTemplateDTO): Promise<ChallengeTemplate> {
    const ref = this.firestore.collection('challengeTemplates').doc()
    
    const template: ChallengeTemplate = {
      id: ref.id,
      type: data.type,
      tier: data.tier,
      title: data.title,
      description: data.description,
      targetValue: data.targetValue,
      reward: data.reward,
      milestoneCount: data.milestoneCount ?? (data.tier === 'platinum' ? 5 : data.tier === 'gold' ? 4 : 3),
      enabled: data.enabled ?? true,
      seasonExclusive: data.seasonExclusive ?? false,
      weight: data.weight ?? 1,
      requirements: data.requirements,
      tags: Array.isArray(data.tags)
        ? (data.tags as unknown[]).map((tag) => String(tag ?? '').trim()).filter(Boolean)
        : [],
      createdAt: new Date(),
      updatedAt: new Date()
    }

    await ref.set(template)
    
    // Invalidate challenge template cache
    await this.invalidateChallengeTemplateCache()

    return template
  }

  /**
   * Update an existing challenge template
   */
  async updateChallengeTemplate(
    templateId: string,
    data: UpdateChallengeTemplateDTO
  ): Promise<ChallengeTemplate> {
    const ref = this.firestore.collection('challengeTemplates').doc(templateId)
    const doc = await ref.get()

    if (!doc.exists) {
      throw new NotFoundError('Challenge template not found', { templateId })
    }

    const updates = {
      ...data,
      updatedAt: new Date()
    }

    await ref.update(updates)

    const updated = await ref.get()
    const template = this.mapDocToChallengeTemplate(updated)

    // Invalidate cache
    await this.invalidateChallengeTemplateCache()

    return template
  }

  /**
   * Delete a challenge template
   */
  async deleteChallengeTemplate(templateId: string): Promise<void> {
    const ref = this.firestore.collection('challengeTemplates').doc(templateId)
    const doc = await ref.get()

    if (!doc.exists) {
      throw new NotFoundError('Challenge template not found', { templateId })
    }

    // Soft delete
    await ref.update({
      enabled: false,
      deletedAt: new Date(),
      updatedAt: new Date()
    })

    // Invalidate cache
    await this.invalidateChallengeTemplateCache()
  }

  /**
   * Get all challenge templates
   */
  async getChallengeTemplates(includeDisabled = false): Promise<ChallengeTemplate[]> {
    let query = this.firestore.collection('challengeTemplates').orderBy('tier').orderBy('type')

    if (!includeDisabled) {
      query = query.where('enabled', '==', true)
    }

    const snapshot = await query.get()
    return snapshot.docs.map((doc: FirestoreDoc) => this.mapDocToChallengeTemplate(doc))
  }

  /**
   * Get challenge template by ID
   */
  async getChallengeTemplateById(templateId: string): Promise<ChallengeTemplate | null> {
    const doc = await this.firestore.collection('challengeTemplates').doc(templateId).get()
    if (!doc.exists) return null
    return this.mapDocToChallengeTemplate(doc)
  }

  // ========== SEASON CONFIGURATION ==========

  /**
   * Create a new season configuration
   */
  async createSeasonConfig(data: CreateSeasonConfigDTO): Promise<SeasonConfig> {
    const ref = this.firestore.collection('seasonConfigs').doc()
    
    const config: SeasonConfig = {
      id: ref.id,
      seasonId: data.seasonId,
      name: data.name,
      description: data.description,
      startDate: data.startDate,
      endDate: data.endDate,
      maxLevel: data.maxLevel ?? 100,
      xpPerLevel: data.xpPerLevel ?? 1000,
      rewards: data.rewards,
      vanActions: data.vanActions,
      active: data.active ?? false,
      createdAt: new Date(),
      updatedAt: new Date()
    }

    await ref.set(config)
    
    // If active, deactivate other seasons
    if (config.active) {
      await this.deactivateOtherSeasons(config.id)
    }

    // Invalidate season cache
    await this.invalidateSeasonCache()

    return config
  }

  /**
   * Update season configuration
   */
  async updateSeasonConfig(
    configId: string,
    data: UpdateSeasonConfigDTO
  ): Promise<SeasonConfig> {
    const ref = this.firestore.collection('seasonConfigs').doc(configId)
    const doc = await ref.get()

    if (!doc.exists) {
      throw new NotFoundError('Season config not found', { configId })
    }

    const updates = {
      ...data,
      updatedAt: new Date()
    }

    await ref.update(updates)

    // If activated, deactivate others
    if (data.active === true) {
      await this.deactivateOtherSeasons(configId)
    }

    const updated = await ref.get()
    const config = this.mapDocToSeasonConfig(updated)

    // Invalidate cache
    await this.invalidateSeasonCache()

    return config
  }

  /**
   * Delete season configuration
   */
  async deleteSeasonConfig(configId: string): Promise<void> {
    const ref = this.firestore.collection('seasonConfigs').doc(configId)
    const doc = await ref.get()

    if (!doc.exists) {
      throw new NotFoundError('Season config not found', { configId })
    }

    await ref.delete()

    // Invalidate cache
    await this.invalidateSeasonCache()
  }

  /**
   * Get all season configurations
   */
  async getSeasonConfigs(): Promise<SeasonConfig[]> {
    const snapshot = await this.firestore
      .collection('seasonConfigs')
      .orderBy('startDate', 'desc')
      .get()

    return snapshot.docs.map((doc: FirestoreDoc) => this.mapDocToSeasonConfig(doc))
  }

  /**
   * Get active season configuration
   */
  async getActiveSeasonConfig(): Promise<SeasonConfig | null> {
    const snapshot = await this.firestore
      .collection('seasonConfigs')
      .where('active', '==', true)
      .limit(1)
      .get()

    if (snapshot.empty) return null
    return this.mapDocToSeasonConfig(snapshot.docs[0])
  }

  // ========== BADGE MANAGEMENT ==========

  /**
   * Create a new badge
   */
  async createBadge(data: CreateBadgeDTO & { createdBy?: string | null }): Promise<BadgeTemplate> {
    const ref = this.firestore.collection('badges').doc(data.id)

    const existing = await ref.get()
    if (existing.exists) {
      throw new ConflictError('Badge id already exists', { badgeId: data.id })
    }

    const sanitizedTags = Array.isArray(data.tags)
      ? Array.from(
          new Set(
            data.tags.map((tag) => {
              if (isString(tag)) return tag.trim()
              if (tag == null) return ''
              return String(tag).trim()
            })
          )
        ).filter(Boolean)
      : []

    const badge: BadgeTemplate = {
      id: data.id as BadgeId, // Cast to BadgeId for dynamic badge creation
      name: data.name,
      description: data.description,
      icon: data.icon,
      rarity: data.rarity,
      requirement: data.requirement ?? undefined,
      accentColor: typeof data.accentColor === 'string'
        ? data.accentColor.trim().length > 0
          ? data.accentColor.trim()
          : null
        : data.accentColor ?? null,
      backgroundGradient: typeof data.backgroundGradient === 'string'
        ? data.backgroundGradient.trim().length > 0
          ? data.backgroundGradient.trim()
          : null
        : data.backgroundGradient ?? null,
      iconUrl: typeof data.iconUrl === 'string'
        ? data.iconUrl.trim().length > 0
          ? data.iconUrl.trim()
          : null
        : data.iconUrl ?? null,
      iconSource: data.iconSource ?? 'emoji',
      category: typeof data.category === 'string'
        ? data.category.trim().length > 0
          ? data.category.trim()
          : null
        : data.category ?? null,
      tags: sanitizedTags,
      aiPrompt: data.aiPrompt ?? null,
      aiModel: data.aiModel ?? null,
      createdBy: data.createdBy ?? null,
      updatedBy: data.createdBy ?? null,
      createdAt: new Date(),
      updatedAt: new Date()
    }

    await ref.set(badge)
    
    // Invalidate badge cache
    await this.redis.del('badges:all')

    return badge
  }

  /**
   * Update badge
   */
  async updateBadge(
    badgeId: string,
    data: UpdateBadgeDTO
  ): Promise<BadgeTemplate> {
    const ref = this.firestore.collection('badges').doc(badgeId)
    const doc = await ref.get()

    if (!doc.exists) {
      throw new NotFoundError('Badge not found', { badgeId })
    }

    // Sanitize fields that differ between DTO and stored BadgeTemplate
    const sanitized: Partial<CreateBadgeDTO> = {
      ...data,
      requirement: data.requirement ?? undefined
    }

    const updates = {
      ...sanitized,
      updatedAt: new Date(),
      updatedBy: data.updatedBy ?? null
    } as Partial<BadgeTemplate>

    if (Array.isArray(data.tags)) {
      updates.tags = Array.from(
        new Set(
          data.tags.map((tag) => {
            if (isString(tag)) return tag.trim()
            if (tag == null) return ''
            return String(tag).trim()
          })
        )
      ).filter(Boolean)
    }

    if ('requirement' in data) {
      if (isString(data.requirement)) {
        const trimmed = data.requirement.trim()
        updates.requirement = trimmed.length > 0 ? trimmed : undefined
      } else {
        updates.requirement = undefined
      }
    }

    if ('accentColor' in data) {
      if (isString(data.accentColor)) {
        const trimmed = data.accentColor.trim()
        updates.accentColor = trimmed.length > 0 ? trimmed : null
      } else if (data.accentColor == null) {
        updates.accentColor = null
      } else {
        const cast = String(data.accentColor).trim()
        updates.accentColor = cast.length > 0 ? cast : null
      }
    }

    if ('backgroundGradient' in data) {
      if (isString(data.backgroundGradient)) {
        const trimmed = data.backgroundGradient.trim()
        updates.backgroundGradient = trimmed.length > 0 ? trimmed : null
      } else if (data.backgroundGradient == null) {
        updates.backgroundGradient = null
      } else {
        const cast = String(data.backgroundGradient).trim()
        updates.backgroundGradient = cast.length > 0 ? cast : null
      }
    }

    if ('iconUrl' in data) {
      if (isString(data.iconUrl)) {
        const trimmed = data.iconUrl.trim()
        updates.iconUrl = trimmed.length > 0 ? trimmed : null
      } else if (data.iconUrl == null) {
        updates.iconUrl = null
      } else {
        const cast = String(data.iconUrl).trim()
        updates.iconUrl = cast.length > 0 ? cast : null
      }
    }

    if ('category' in data) {
      if (isString(data.category)) {
        const trimmed = data.category.trim()
        updates.category = trimmed.length > 0 ? trimmed : null
      } else if (data.category == null) {
        updates.category = null
      } else {
        const cast = String(data.category).trim()
        updates.category = cast.length > 0 ? cast : null
      }
    }

    if ('requirement' in data) {
      const requirement = data.requirement
      updates.requirement =
        isNonEmptyString(requirement)
          ? requirement.trim()
          : undefined
    }

    await ref.update(updates)

    const updated = await ref.get()
    const badge = updated.data() as BadgeTemplate

    // Invalidate cache
    await this.redis.del('badges:all')

    return badge
  }

  /**
   * Delete badge
   */
  async deleteBadge(badgeId: string): Promise<void> {
    const ref = this.firestore.collection('badges').doc(badgeId)
    const doc = await ref.get()

    if (!doc.exists) {
      throw new NotFoundError('Badge not found', { badgeId })
    }

    await ref.delete()

    // Invalidate cache
    await this.redis.del('badges:all')
  }

  /**
   * Get all badges
   */
  async getAllBadges(): Promise<BadgeTemplate[]> {
    const cachedRaw = (await this.redis.get('badges:all')) as unknown
    if (isNonEmptyString(cachedRaw)) {
      try {
        const parsed = JSON.parse(cachedRaw) as Array<Record<string, unknown>>
        return parsed.map((entry) => this.mapDocToBadgeTemplate(entry, String(entry.id ?? entry.badgeId ?? 'badge')))
      } catch (error) {
        this.logger.warn('Failed to parse badges cache, invalidating', {
          error: (error as Error).message
        })
        await this.redis.del('badges:all')
      }
    } else if (Array.isArray(cachedRaw)) {
      try {
        return (cachedRaw as Array<Record<string, unknown>>).map((entry) =>
          this.mapDocToBadgeTemplate(entry, String(entry.id ?? entry.badgeId ?? 'badge'))
        )
      } catch (error) {
        this.logger.warn('Badges cache array could not be normalized, invalidating', {
          error: (error as Error).message
        })
        await this.redis.del('badges:all')
      }
    } else if (cachedRaw != null) {
      this.logger.warn('Badges cache is not a string, invalidating', {
        type: typeof cachedRaw
      })
      await this.redis.del('badges:all')
    }

    const snapshot = await this.firestore
      .collection('badges')
      .orderBy('rarity')
      .orderBy('name')
      .get()

    const templates = snapshot.docs.map((doc: FirestoreDoc) =>
      this.mapDocToBadgeTemplate(doc.data() as Record<string, unknown>, doc.id)
    )

    await this.redis.setex(
      'badges:all',
      3600,
      JSON.stringify(templates.map((template: BadgeTemplate) => this.serializeBadgeTemplate(template)))
    )

    return templates
  }

  async generateBadgeConcept(data: {
    preset?: string
    prompt?: string
    tone?: string
    audience?: string
    rarity?: 'common' | 'rare' | 'epic' | 'legendary'
    locale?: string
    style?: string
    generateImage?: boolean
    iconVariant?: 'flat' | '3d' | 'outlined'
  }): Promise<{
    concept: ReturnType<OpenAIService['generateBadgeConcept']> extends Promise<infer R> ? R : never
    imageBase64?: string
  }> {
    const concept = await this.openai.generateBadgeConcept(
      {
        preset: data.preset,
        prompt: data.prompt,
        tone: data.tone,
        audience: data.audience,
        rarity: data.rarity,
        locale: data.locale,
        style: data.style
      },
      { feature: 'admin:badge-concept' }
    )

    let imageBase64: string | undefined
    if (data.generateImage) {
      try {
        imageBase64 = await this.openai.generateBadgeIconImage({
          prompt: concept.iconPrompt || concept.aiPrompt,
          variant: data.iconVariant,
          size: '512x512'
        })
      } catch (error) {
        this.logger.warn('Badge icon image generation failed; continuing without image', {
          error: (error as Error).message
        })
      }
    }

    return { concept, imageBase64 }
  }

  private normalizeDate(value: unknown): Date {
    if (value instanceof Date) return value
    if (isString(value)) {
      const parsed = new Date(value)
      if (!Number.isNaN(parsed.getTime())) return parsed
    }
    if (value && typeof (value as any).toDate === 'function') {
      try {
        const converted = (value as any).toDate()
        if (converted instanceof Date) return converted
      } catch {
        // ignore
      }
    }
    return new Date()
  }

  private mapDocToBadgeTemplate(raw: Record<string, unknown>, fallbackId: string): BadgeTemplate {
    const tags = Array.isArray(raw.tags)
      ? Array.from(
          new Set(
            (raw.tags as unknown[]).map((tag) => {
              if (isString(tag)) return tag.trim()
              if (tag == null) return ''
              return String(tag).trim()
            })
          )
        ).filter(Boolean)
      : []

    const rarityValue =
      typeof raw.rarity === 'string' ? raw.rarity.trim().toLowerCase() : raw.rarity
    const validRarity: BadgeTemplate['rarity'] =
      rarityValue === 'rare' || rarityValue === 'epic' || rarityValue === 'legendary'
        ? rarityValue
        : 'common'

    const iconSourceRaw = typeof raw.iconSource === 'string' ? raw.iconSource.trim().toLowerCase() : null
    const iconSource: BadgeTemplate['iconSource'] =
      iconSourceRaw === 'vector' || iconSourceRaw === 'image' ? iconSourceRaw : 'emoji'

    return {
      id: String(raw.id ?? fallbackId) as BadgeId,
      name: typeof raw.name === 'string' ? raw.name.trim() : String(raw.name ?? 'Badge'),
      description:
        typeof raw.description === 'string'
          ? raw.description.trim()
          : String(raw.description ?? ''),
      icon: String(raw.icon ?? '🏅'),
      rarity: validRarity,
      requirement:
        isNonEmptyString(raw.requirement)
          ? raw.requirement.trim()
          : undefined,
      accentColor:
        typeof raw.accentColor === 'string'
          ? raw.accentColor.trim().length > 0
            ? raw.accentColor.trim()
            : null
          : null,
      backgroundGradient:
        typeof raw.backgroundGradient === 'string'
          ? raw.backgroundGradient.trim().length > 0
            ? raw.backgroundGradient.trim()
            : null
          : null,
      iconUrl:
        typeof raw.iconUrl === 'string'
          ? raw.iconUrl.trim().length > 0
            ? raw.iconUrl.trim()
            : null
          : null,
      iconSource,
      category:
        typeof raw.category === 'string'
          ? raw.category.trim().length > 0
            ? raw.category.trim()
            : null
          : null,
      tags,
      aiPrompt:
        typeof raw.aiPrompt === 'string'
          ? raw.aiPrompt.trim().length > 0
            ? raw.aiPrompt.trim()
            : null
          : null,
      aiModel:
        typeof raw.aiModel === 'string'
          ? raw.aiModel.trim().length > 0
            ? raw.aiModel.trim()
            : null
          : null,
      createdBy:
        typeof raw.createdBy === 'string'
          ? raw.createdBy.trim().length > 0
            ? raw.createdBy.trim()
            : null
          : null,
      updatedBy:
        typeof raw.updatedBy === 'string'
          ? raw.updatedBy.trim().length > 0
            ? raw.updatedBy.trim()
            : null
          : null,
      createdAt: this.normalizeDate(raw.createdAt),
      updatedAt: this.normalizeDate(raw.updatedAt)
    }
  }

  private serializeBadgeTemplate(template: BadgeTemplate): Record<string, unknown> {
    return {
      ...template,
      createdAt: template.createdAt.toISOString(),
      updatedAt: template.updatedAt.toISOString()
    }
  }

  /**
   * Assign badge to user
   */
  async assignBadgeToUser(userId: string, badgeId: string): Promise<void> {
    const userRef = this.firestore.collection('users').doc(userId)
    const userDoc = await userRef.get()

    if (!userDoc.exists) {
      throw new NotFoundError('User not found', { userId })
    }

    const currentRaw = userDoc.data()?.badges ?? []
    const currentBadges = Array.isArray(currentRaw)
      ? (currentRaw as unknown[]).map((badge) => {
          if (isString(badge)) return badge
          if (badge && typeof (badge as { id?: string }).id === 'string') {
            return (badge as { id: string }).id
          }
          return String(badge ?? '').trim()
        }).filter(Boolean)
      : []

    if (currentBadges.includes(badgeId)) {
      throw new ConflictError('User already has this badge', { userId, badgeId })
    }

    await userRef.update({
      badges: [...currentBadges, badgeId],
      updatedAt: new Date()
    })

    // Invalidate user cache
    await this.redis.del(`profile:${userId}`)
  }

  /**
   * Remove badge from user
   */
  async removeBadgeFromUser(userId: string, badgeId: string): Promise<void> {
    const userRef = this.firestore.collection('users').doc(userId)
    const userDoc = await userRef.get()

    if (!userDoc.exists) {
      throw new NotFoundError('User not found', { userId })
    }

    const currentRaw = userDoc.data()?.badges ?? []
    const currentBadges = Array.isArray(currentRaw)
      ? (currentRaw as unknown[]).map((badge) => {
          if (isString(badge)) return badge
          if (badge && typeof (badge as { id?: string }).id === 'string') {
            return (badge as { id: string }).id
          }
          return String(badge ?? '').trim()
        }).filter(Boolean)
      : []

    const filtered = currentBadges.filter((b) => b !== badgeId)

    await userRef.update({
      badges: filtered,
      updatedAt: new Date()
    })

    // Invalidate user cache
    await this.redis.del(`profile:${userId}`)
  }

  // ========== BULK OPERATIONS ==========

  /**
   * Import quest templates (bulk create)
   */
  async importQuestTemplates(templates: CreateQuestTemplateDTO[]): Promise<QuestTemplate[]> {
    const batch = createBatchBuilder(this.firestore)
    const createdTemplates: QuestTemplate[] = []

    for (const data of templates) {
      const template: Omit<QuestTemplate, 'id'> = {
        type: data.type,
        category: data.category,
        title: data.title,
        description: data.description,
        difficulty: data.difficulty,
        targetValue: data.targetValue,
        reward: data.reward,
        enabled: data.enabled ?? true,
        weight: data.weight ?? 1,
        requirements: data.requirements,
        tags: data.tags ?? [],
        createdAt: new Date(),
        updatedAt: new Date()
      }

      const id = batch.addSet('questTemplates', null, template)
      createdTemplates.push({ id, ...template })
    }

    await batch.commit()

    // Invalidate cache
    await this.invalidateQuestTemplateCache()

    return createdTemplates
  }

  /**
   * Import challenge templates (bulk create)
   */
  async importChallengeTemplates(templates: CreateChallengeTemplateDTO[]): Promise<ChallengeTemplate[]> {
    const batch = createBatchBuilder(this.firestore)
    const createdTemplates: ChallengeTemplate[] = []

    for (const data of templates) {
      const template: Omit<ChallengeTemplate, 'id'> = {
        type: data.type,
        tier: data.tier,
        title: data.title,
        description: data.description,
        targetValue: data.targetValue,
        reward: data.reward,
        milestoneCount: data.milestoneCount ?? (data.tier === 'platinum' ? 5 : data.tier === 'gold' ? 4 : 3),
        enabled: data.enabled ?? true,
        seasonExclusive: data.seasonExclusive ?? false,
        weight: data.weight ?? 1,
        requirements: data.requirements,
        tags: data.tags ?? [],
        createdAt: new Date(),
        updatedAt: new Date()
      }

      const id = batch.addSet('challengeTemplates', null, template)
      createdTemplates.push({ id, ...template })
    }

    await batch.commit()

    // Invalidate cache
    await this.invalidateChallengeTemplateCache()

    return createdTemplates
  }

  /**
   * Export all templates (for backup/migration)
   */
  async exportAllTemplates(): Promise<{
    quests: QuestTemplate[]
    challenges: ChallengeTemplate[]
    seasons: SeasonConfig[]
    badges: BadgeTemplate[]
  }> {
    const [quests, challenges, seasons, badges] = await Promise.all([
      this.getQuestTemplates(true),
      this.getChallengeTemplates(true),
      this.getSeasonConfigs(),
      this.getAllBadges()
    ])

    return { quests, challenges, seasons, badges }
  }

  // ========== USER QUEST/CHALLENGE MANAGEMENT ==========

  /**
   * Force complete a user quest (admin override)
   */
  async forceCompleteQuest(questId: string): Promise<void> {
    const ref = this.firestore.collection('quests').doc(questId)
    const doc = await ref.get()

    if (!doc.exists) {
      throw new NotFoundError('Quest not found', { questId })
    }

    await ref.update({
      status: 'completed',
      currentValue: doc.data()?.targetValue ?? 0,
      completedAt: new Date(),
      updatedAt: new Date()
    })

    // Invalidate cache
    const userId = doc.data()?.userId
    if (userId) {
      await this.redis.del(`quests:active:${userId}`)
      await this.redis.del(`quest:${questId}`)
    }
  }

  /**
   * Force complete a user challenge (admin override)
   */
  async forceCompleteChallenge(challengeId: string): Promise<void> {
    const ref = this.firestore.collection('challenges').doc(challengeId)
    const doc = await ref.get()

    if (!doc.exists) {
      throw new NotFoundError('Challenge not found', { challengeId })
    }

    await ref.update({
      status: 'completed',
      currentValue: doc.data()?.targetValue ?? 0,
      completedAt: new Date(),
      updatedAt: new Date()
    })

    // Invalidate cache
    const userId = doc.data()?.userId
    const seasonId = doc.data()?.seasonId
    if (userId && seasonId) {
      await this.redis.del(`challenges:season:${userId}:${seasonId}`)
      await this.redis.del(`challenge:${challengeId}`)
    }
  }

  /**
   * Delete user quest (admin cleanup)
   */
  async deleteUserQuest(questId: string): Promise<void> {
    const ref = this.firestore.collection('quests').doc(questId)
    const doc = await ref.get()

    if (!doc.exists) {
      throw new NotFoundError('Quest not found', { questId })
    }

    const userId = doc.data()?.userId
    await ref.delete()

    // Invalidate cache
    if (userId) {
      await this.redis.del(`quests:active:${userId}`)
      await this.redis.del(`quest:${questId}`)
    }
  }

  /**
   * Delete user challenge (admin cleanup)
   */
  async deleteUserChallenge(challengeId: string): Promise<void> {
    const ref = this.firestore.collection('challenges').doc(challengeId)
    const doc = await ref.get()

    if (!doc.exists) {
      throw new NotFoundError('Challenge not found', { challengeId })
    }

    const userId = doc.data()?.userId
    const seasonId = doc.data()?.seasonId
    await ref.delete()

    // Invalidate cache
    if (userId && seasonId) {
      await this.redis.del(`challenges:season:${userId}:${seasonId}`)
      await this.redis.del(`challenge:${challengeId}`)
    }
  }

  // ========== STATS & ANALYTICS ==========

  /**
   * Get quest statistics
   */
  async getQuestStats(): Promise<{
    totalTemplates: number
    enabledTemplates: number
    totalUserQuests: number
    completedQuests: number
    activeQuests: number
    completionRate: number
  }> {
    const [templatesSnapshot, questsSnapshot, completedSnapshot] = await Promise.all([
      this.firestore.collection('questTemplates').where('enabled', '==', true).get(),
      this.firestore.collection('quests').get(),
      this.firestore.collection('quests').where('status', '==', 'completed').get()
    ])

    const totalUserQuests = questsSnapshot.size
    const completedQuests = completedSnapshot.size
    const completionRate = totalUserQuests > 0 ? (completedQuests / totalUserQuests) * 100 : 0

    return {
      totalTemplates: templatesSnapshot.size,
      enabledTemplates: templatesSnapshot.size,
      totalUserQuests,
      completedQuests,
      activeQuests: totalUserQuests - completedQuests,
      completionRate: Math.round(completionRate * 100) / 100
    }
  }

  /**
   * Get challenge statistics
   */
  async getChallengeStats(): Promise<{
    totalTemplates: number
    enabledTemplates: number
    totalUserChallenges: number
    completedChallenges: number
    activeChallenges: number
    completionRate: number
  }> {
    const [templatesSnapshot, challengesSnapshot, completedSnapshot] = await Promise.all([
      this.firestore.collection('challengeTemplates').where('enabled', '==', true).get(),
      this.firestore.collection('challenges').get(),
      this.firestore.collection('challenges').where('status', '==', 'completed').get()
    ])

    const totalUserChallenges = challengesSnapshot.size
    const completedChallenges = completedSnapshot.size
    const completionRate = totalUserChallenges > 0 ? (completedChallenges / totalUserChallenges) * 100 : 0

    return {
      totalTemplates: templatesSnapshot.size,
      enabledTemplates: templatesSnapshot.size,
      totalUserChallenges,
      completedChallenges,
      activeChallenges: totalUserChallenges - completedChallenges,
      completionRate: Math.round(completionRate * 100) / 100
    }
  }

  // ========== PRIVATE HELPERS ==========

  private async deactivateOtherSeasons(activeSeasonId: string): Promise<void> {
    const snapshot = await this.firestore
      .collection('seasonConfigs')
      .where('active', '==', true)
      .get()

    const batch = createBatchBuilder(this.firestore)
    for (const doc of snapshot.docs) {
      if (doc.id !== activeSeasonId) {
        batch.updateRef(doc.ref, { active: false, updatedAt: new Date() })
      }
    }

    await batch.commit()
  }

  private async invalidateQuestTemplateCache(): Promise<void> {
    await this.redis.del('questTemplates:all')
    await this.redis.del('questTemplates:daily')
    await this.redis.del('questTemplates:weekly')
  }

  private async invalidateChallengeTemplateCache(): Promise<void> {
    await this.redis.del('challengeTemplates:all')
    await this.redis.del('challengeTemplates:bronze')
    await this.redis.del('challengeTemplates:silver')
    await this.redis.del('challengeTemplates:gold')
    await this.redis.del('challengeTemplates:platinum')
  }

  private async invalidateSeasonCache(): Promise<void> {
    await this.redis.del('season:active')
    await this.redis.del('season:configs')
  }

  private mapDocToQuestTemplate(doc: FirestoreDocLike): QuestTemplate {
    if (!doc.exists) {
      throw new NotFoundError('Document does not exist', { docId: doc.id })
    }
    const data = doc.data()!

    const questTypeRaw = typeof data.type === 'string' ? data.type.trim().toLowerCase() : ''
    const questType: QuestTemplate['type'] =
      questTypeRaw === 'stops' ||
      questTypeRaw === 'ai_usage' ||
      questTypeRaw === 'sharing' ||
      questTypeRaw === 'gamification'
        ? questTypeRaw
        : 'distance'

    const categoryRaw = typeof data.category === 'string' ? data.category.trim().toLowerCase() : ''
    const category: QuestTemplate['category'] = categoryRaw === 'weekly' ? 'weekly' : 'daily'

    const difficultyRaw = typeof data.difficulty === 'string' ? data.difficulty.trim().toLowerCase() : ''
    const difficulty: QuestTemplate['difficulty'] =
      difficultyRaw === 'easy' || difficultyRaw === 'hard' ? (difficultyRaw as QuestTemplate['difficulty']) : 'medium'

    const rewardRaw = (data.reward ?? {}) as Record<string, unknown>
    const reward = {
      points: Math.max(0, Number(rewardRaw.points ?? 0)),
      xp: rewardRaw.xp != null ? Math.max(0, Number(rewardRaw.xp)) : undefined,
      seasonXP: rewardRaw.seasonXP != null ? Math.max(0, Number(rewardRaw.seasonXP)) : undefined,
      badge:
        isNonEmptyString(rewardRaw.badge)
          ? rewardRaw.badge.trim()
          : undefined
    }

    const requirementsRaw = data.requirements
    const requirements =
      requirementsRaw && typeof requirementsRaw === 'object'
        ? {
            minLevel:
              (requirementsRaw as { minLevel?: unknown }).minLevel != null
                ? Math.max(0, Number((requirementsRaw as { minLevel?: unknown }).minLevel))
                : undefined,
            requiredBadges: Array.isArray((requirementsRaw as { requiredBadges?: unknown }).requiredBadges)
              ? ((requirementsRaw as { requiredBadges: unknown[] }).requiredBadges).map((badge) =>
                  String(badge ?? '').trim()
                ).filter(Boolean)
              : undefined,
            excludeIfHasBadges: Array.isArray((requirementsRaw as { excludeIfHasBadges?: unknown }).excludeIfHasBadges)
              ? ((requirementsRaw as { excludeIfHasBadges: unknown[] }).excludeIfHasBadges).map((badge) =>
                  String(badge ?? '').trim()
                ).filter(Boolean)
              : undefined
          }
        : undefined

    const weight = Math.min(10, Math.max(1, Number(data.weight ?? 1)))
    const targetValue = Math.max(0, Number(data.targetValue ?? 0))

    return {
      id: doc.id,
      type: questType,
      category,
      title:
        isNonEmptyString(data.title)
          ? data.title.trim()
          : `Quest ${doc.id}`,
      description:
        isNonEmptyString(data.description)
          ? data.description.trim()
          : 'Quest générée automatiquement',
      difficulty,
      targetValue,
      reward,
      enabled: Boolean(data.enabled ?? true),
      weight,
      requirements,
      tags: Array.isArray(data.tags)
        ? (data.tags as unknown[]).map((tag) => String(tag ?? '').trim()).filter(Boolean)
        : [],
      createdAt: this.normalizeDate(data.createdAt),
      updatedAt: this.normalizeDate(data.updatedAt)
    }
  }

  private mapDocToChallengeTemplate(doc: FirestoreDocLike): ChallengeTemplate {
    if (!doc.exists) {
      throw new NotFoundError('Document does not exist', { docId: doc.id })
    }
    const data = doc.data()!

    const challengeTypeRaw = typeof data.type === 'string' ? data.type.trim().toLowerCase() : ''
    const challengeType: ChallengeTemplate['type'] =
      challengeTypeRaw === 'stops_visited' ||
      challengeTypeRaw === 'plans_created' ||
      challengeTypeRaw === 'ai_usage' ||
      challengeTypeRaw === 'social_shares'
        ? challengeTypeRaw
        : 'distance_total'

    const tierRaw = typeof data.tier === 'string' ? data.tier.trim().toLowerCase() : ''
    const tier: ChallengeTemplate['tier'] =
      tierRaw === 'silver' || tierRaw === 'gold' || tierRaw === 'platinum' ? tierRaw : 'bronze'

    const rewardRaw = (data.reward ?? {}) as Record<string, unknown>
    const reward = {
      points: Math.max(0, Number(rewardRaw.points ?? 0)),
      xp: Math.max(0, Number(rewardRaw.xp ?? 0)),
      seasonXP: Math.max(0, Number(rewardRaw.seasonXP ?? 0)),
      badge:
        isNonEmptyString(rewardRaw.badge)
          ? rewardRaw.badge.trim()
          : undefined
    }

    const requirementsRaw = data.requirements
    const requirements =
      requirementsRaw && typeof requirementsRaw === 'object'
        ? {
            minLevel:
              (requirementsRaw as { minLevel?: unknown }).minLevel != null
                ? Math.max(0, Number((requirementsRaw as { minLevel?: unknown }).minLevel))
                : undefined,
            requiredBadges: Array.isArray((requirementsRaw as { requiredBadges?: unknown }).requiredBadges)
              ? ((requirementsRaw as { requiredBadges: unknown[] }).requiredBadges).map((badge) =>
                  String(badge ?? '').trim()
                ).filter(Boolean)
              : undefined,
            excludeIfHasBadges: Array.isArray((requirementsRaw as { excludeIfHasBadges?: unknown }).excludeIfHasBadges)
              ? ((requirementsRaw as { excludeIfHasBadges: unknown[] }).excludeIfHasBadges).map((badge) =>
                  String(badge ?? '').trim()
                ).filter(Boolean)
              : undefined
          }
        : undefined

    const milestoneCount = Math.min(5, Math.max(3, Number(data.milestoneCount ?? 3)))
    const targetValue = Math.max(0, Number(data.targetValue ?? 0))
    const weight = Math.min(10, Math.max(1, Number(data.weight ?? 1)))

    return {
      id: doc.id,
      type: challengeType,
      tier,
      title:
        isNonEmptyString(data.title)
          ? data.title.trim()
          : `Challenge ${doc.id}`,
      description:
        isNonEmptyString(data.description)
          ? data.description.trim()
          : 'Challenge généré automatiquement',
      targetValue,
      reward,
      milestoneCount,
      enabled: Boolean(data.enabled ?? true),
      seasonExclusive: Boolean(data.seasonExclusive ?? false),
      weight,
      requirements,
      tags: Array.isArray(data.tags)
        ? (data.tags as unknown[]).map((tag) => String(tag ?? '').trim()).filter(Boolean)
        : [],
      createdAt: this.normalizeDate(data.createdAt),
      updatedAt: this.normalizeDate(data.updatedAt)
    }
  }

  private mapDocToSeasonConfig(doc: FirestoreDocLike): SeasonConfig {
    if (!doc.exists) {
      throw new NotFoundError('Document does not exist', { docId: doc.id })
    }
    const data = doc.data()!
    return {
      id: doc.id,
      seasonId: data.seasonId,
      name: data.name,
      description: data.description,
      startDate: this.normalizeDate(data.startDate),
      endDate: this.normalizeDate(data.endDate),
      maxLevel: data.maxLevel ?? 100,
      xpPerLevel: data.xpPerLevel ?? 1000,
      rewards: data.rewards,
      vanActions: data.vanActions,
      active: data.active ?? false,
      createdAt: this.normalizeDate(data.createdAt),
      updatedAt: this.normalizeDate(data.updatedAt)
    }
  }
}
